import { createAuth0Client, type Auth0Client, type RedirectLoginOptions } from '@auth0/auth0-spa-js'
import { config } from '@/config'
import { devBypassEnabled, devMockFetch, devMockUser } from '@/auth/devBypass'

let clientPromise: Promise<Auth0Client> | null = null

function getRedirectUri(): string {
	return window.location.origin + '/signin'
}

// Scope set mirrors the mobile client (med-mobile) and med-web-funnel:
// - `openid profile email` — OIDC basics
// - `offline_access`       — requests a refresh token (terraform grants it)
// - `access:app`           — granted to the med backend audience in terraform
const AUTH0_SCOPE = 'openid profile email offline_access access:app'

/**
 * Thrown when the SDK could not produce a valid access token because the Auth0
 * session is no longer recoverable (refresh token missing, expired, or revoked).
 *
 * Callers should treat this as "you are logged out" — clear local auth state and
 * redirect to /signin. Do not retry transparently.
 */
export class Auth0SessionExpiredError extends Error {
	constructor(cause: unknown) {
		const causeMessage = cause instanceof Error ? cause.message : String(cause)
		super(`Auth0 session expired or unrecoverable: ${causeMessage}`)
		this.name = 'Auth0SessionExpiredError'
	}
}

/** Auth0 SDK error messages/codes that mean the session is gone. */
const SESSION_GONE_PATTERNS = [
	'Missing Refresh Token',
	'login_required',
	'invalid_grant',
	'consent_required',
	'interaction_required',
	'unknown_user',
] as const

function isSessionGoneError(err: unknown): boolean {
	const message = err instanceof Error ? err.message : String(err ?? '')
	const code = (err as { error?: string } | null)?.error ?? ''
	const haystack = `${message} ${code}`.toLowerCase()
	return SESSION_GONE_PATTERNS.some((p) => haystack.includes(p.toLowerCase()))
}

function getClient(): Promise<Auth0Client> {
	if (!clientPromise) {
		clientPromise = createAuth0Client({
			domain: config.auth0Domain,
			clientId: config.auth0ClientId,
			// DevTools will show TWO POST /oauth/token requests on every login:
			//   1) 400 with body {"error":"use_dpop_nonce"} — server demands a nonce
			//   2) 204 OPTIONS preflight, then 200 with tokens — SDK retries with nonce
			// This is the RFC 9449 §8 (DPoP-Nonce) two-step dance, not a real failure.
			useDpop: true,
			// Without this the SDK falls back to iframe silent-auth on token refresh,
			// which fails under third-party-cookie blocking and breaks fetchWithAuth.
			useRefreshTokens: true,
			// Persist the refresh token across page reloads. Safe with DPoP because
			// access tokens are bound to a private key kept in sessionStorage —
			// an XSS-stolen token from localStorage cannot be replayed without it.
			cacheLocation: 'localstorage',
			authorizationParams: {
				audience: config.auth0Audience,
				scope: AUTH0_SCOPE,
				redirect_uri: getRedirectUri(),
			},
		})
	}
	return clientPromise
}

export interface CallbackResult {
	/** appState passed to loginWithRedirect, opaquely round-tripped through Auth0. */
	appState: { next?: string } | undefined
}

/**
 * If the current URL is an Auth0 redirect callback (`?code=…&state=…`), exchange
 * the code for tokens and strip the query. Returns the appState we attached at
 * login time (containing the post-login `next` URL), or undefined if this wasn't
 * a callback.
 *
 * Safe to call on every page load — it's a no-op without code+state.
 */
export async function checkAuth0RedirectCallback(): Promise<CallbackResult | null> {
	if (devBypassEnabled) return null
	const query = window.location.search
	if (!(query.includes('code=') && query.includes('state='))) return null

	const client = await getClient()
	const result = await client.handleRedirectCallback<{ next?: string }>()
	window.history.replaceState({}, document.title, window.location.pathname)
	return { appState: result.appState }
}

export interface LoginAuth0Options {
	/** Pre-fills the identifier field on the Universal Login form. */
	email?: string
	/** Path to return to after successful login. Round-tripped via Auth0 appState. */
	next?: string
}

export async function loginAuth0({ email, next }: LoginAuth0Options = {}): Promise<void> {
	if (devBypassEnabled) return
	const client = await getClient()
	const options: RedirectLoginOptions<{ next?: string }> = {
		authorizationParams: {
			redirect_uri: getRedirectUri(),
			...(email ? { login_hint: email } : {}),
		},
	}
	if (next) options.appState = { next }
	await client.loginWithRedirect(options)
}

// Default to `${origin}/signin` because that path is what's allowlisted in
// `allowed_logout_urls` on the Auth0 client (terraform reuses the same list as
// the login callbacks — see med-terraform stacks/shared/auth0/_module/clients.tf).
// Using a bare origin here would fail with `Callback URL mismatch` in real Auth0.
function getLogoutReturnTo(): string {
	return window.location.origin + '/signin'
}

export async function logoutAuth0(returnTo: string = getLogoutReturnTo()): Promise<void> {
	if (devBypassEnabled) {
		window.location.assign(returnTo)
		return
	}
	const client = await getClient()
	await client.logout({ logoutParams: { returnTo } })
}

export async function isAuth0Authenticated(): Promise<boolean> {
	if (devBypassEnabled) return true
	const client = await getClient()
	return client.isAuthenticated()
}

export async function getAuth0User(): Promise<unknown> {
	if (devBypassEnabled) return devMockUser
	const client = await getClient()
	return client.getUser()
}

/**
 * Make an authenticated DPoP-bound request to a backend that accepts our Auth0 access token.
 *
 * Why this exists instead of using `createFetcher().fetchWithAuth(url, init)` directly:
 * the SDK's internal `getTokenSilently` does NOT inherit `audience` from the constructor's
 * `authorizationParams` — without explicit authParams it throws
 * `Missing Refresh Token (audience: '', scope: ...)`. So we pass audience+scope explicitly.
 *
 * If the SDK fails to mint a token because the session is unrecoverable, this throws
 * Auth0SessionExpiredError. HTTP-level 4xx/5xx from the backend itself bubble up unchanged.
 *
 * @param dpopNonceId  stable string per backend; reused across requests to share the nonce cache
 */
export async function auth0FetchWithAuth(dpopNonceId: string, url: string, init?: RequestInit): Promise<Response> {
	if (devBypassEnabled) return devMockFetch(url, init)
	const client = await getClient()
	const fetcher = client.createFetcher({ dpopNonceId })
	try {
		return await fetcher.fetchWithAuth(url, init, {
			audience: config.auth0Audience,
			scope: AUTH0_SCOPE.split(' '),
		})
	} catch (err) {
		if (isSessionGoneError(err)) throw new Auth0SessionExpiredError(err)
		throw err
	}
}
